// Centralised MUI theme factory for ParlSearch — "Reading Room" design direction.
//
// A single `buildTheme(mode)` produces either the light or dark theme from one
// shared token set, so the two modes stay in sync. This is the primary lever
// for the site-wide visual direction: reworking the palette, typography and
// component overrides here propagates the new aesthetic to every MUI-based
// page (search, detail, Explore stats, home) automatically.
//
// The Reading Room direction (Trove-inspired library catalogue): ONE sans
// family (Source Sans 3) for everything you operate — body, nav, labels,
// kickers, table heads, dates, counts, buttons, chips, tooltips; a matched
// serif (Source Serif 4) for TITLES ONLY — h1–h4, the Home hero, browse-column
// heads, result titles; NO monospace anywhere in the UI (Source Code Pro is
// kept exported solely for code samples on the API docs page). White ground
// and white surfaces, hairline 1px separators (not heavy ink rules), 600-weight
// headings with normal tracking, sentence case everywhere (no uppercase
// labels, no tracked-out small caps), and a single accent — Ming teal #006A78 —
// carrying links and result titles. Numbers use tabular figures where they
// line up in columns, instead of switching to a mono face.
//
// `theme.palette.ledger.*` remains the token NAMESPACE name for compatibility
// with existing consumers — it now carries the Reading Room values.
//
// Components should prefer theme tokens (background.paper / background.default /
// text.primary / text.secondary / divider / primary.main) over hardcoded
// colours so they invert automatically with the mode. Use UI_FONT where a
// component must name the sans explicitly, DISPLAY_FONT for titles only, and
// `fontVariantNumeric: 'tabular-nums'` (not MONO) for aligned figures.
//
// NB: the chamber-colour chart convention (House green / Senate red in
// charts.js via chamberColor()/seriesColor()) is independent of the MUI
// palette and is unaffected by this file.

import { createTheme } from '@mui/material/styles';

// Font stacks (loaded via <link> in public/index.html).
export const UI_FONT = "'Source Sans 3', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const DISPLAY_FONT = "'Source Serif 4', Georgia, 'Times New Roman', serif";   // titles only
export const MONO = "'Source Code Pro', ui-monospace, Menlo, monospace";             // code samples ONLY (API docs); not a UI font

// Corner radius for buttons, cards and inputs (Reading Room: 6).
const RADIUS = 6;

// Reading Room token set per mode (namespace stays `ledger` for compatibility).
// Light is the primary direction — white ground, neutral greys, hairline
// #DEE2E6 separators, Ming teal accent. Dark is a coherent derivation so dark
// mode keeps working site-wide rather than regressing.
const TOKENS = {
  light: {
    accent: '#006A78',        // Ming teal — the one accent: links, result titles, active nav
    accentText: '#006A78',    // 6.3:1 on white — same colour serves small text
    accentWeak: '#E3F1F2',    // selection / selected-row tint
    onAccent: '#FFFFFF',
    bg: '#FFFFFF',
    surface: '#FFFFFF',
    ink: '#212529',
    muted: '#535353',
    faint: '#6D6D6D',
    line: '#DEE2E6',          // hairline separators
    ruleColor: '#DEE2E6',
    ruleWidth: '1px',
    fieldWidth: '1px',
    fieldColor: '#CED4DA',    // input / outlined-button borders
    hover: '#F4F4F4',
    // Pending / caution status pill.
    pendingBg: '#F7E8C8',
    pendingInk: '#8A5A00',
  },
  dark: {
    accent: '#4FB3C1',
    accentText: '#4FB3C1',
    accentWeak: 'rgba(79,179,193,0.16)',
    onAccent: '#0E1A1C',
    bg: '#121417',
    surface: '#1A1D21',
    ink: '#E9ECEF',
    muted: '#ADB5BD',
    faint: '#868E96',
    line: '#2F343A',
    ruleColor: '#2F343A',
    ruleWidth: '1px',
    fieldWidth: '1px',
    fieldColor: '#3A4047',
    hover: 'rgba(255,255,255,0.05)',
    pendingBg: 'rgba(240,200,120,0.16)',
    pendingInk: '#E4B860',
  },
};

function buildPalette(mode) {
  const t = TOKENS[mode] || TOKENS.light;
  return {
    mode,
    primary: {
      main: t.accent,
      light: t.accent,
      dark: t.accent,
      contrastText: t.onAccent,
    },
    // Secondary maps to the muted ink so MUI's "secondary" surfaces read as
    // quiet neutrals rather than a competing hue (one-accent discipline).
    secondary: {
      main: t.muted,
      light: t.faint,
      dark: t.ink,
      contrastText: t.onAccent,
    },
    text: {
      primary: t.ink,
      secondary: t.muted,
      disabled: t.faint,
    },
    background: {
      default: t.bg,
      paper: t.surface,
    },
    divider: t.line,
    // Custom design tokens (Reading Room values; namespace kept as theme.palette.ledger.* for compatibility).
    ledger: { ...t },
    action: mode === 'dark'
      ? { hover: t.hover, selected: t.accentWeak, active: t.ink }
      : { hover: t.hover, selected: t.accentWeak, active: t.ink },
  };
}

// Leaflet basemap tiles per mode (#159). Light keeps standard OSM; dark uses
// CARTO's dark_all raster tiles so maps stay legible against the dark UI.
export const BASEMAPS = {
  light: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
  },
};

export function basemapFor(mode) {
  return mode === 'dark' ? BASEMAPS.dark : BASEMAPS.light;
}

export function buildTheme(mode = 'light') {
  const palette = buildPalette(mode);
  const t = palette.ledger;

  return createTheme({
    palette,
    shape: { borderRadius: RADIUS },
    typography: {
      fontFamily: UI_FONT,
      // Titles (h1–h4) in the display serif; h5/h6 stay in the UI sans.
      h1: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: 0, lineHeight: 1.15, color: t.ink },
      h2: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: 0, lineHeight: 1.2, color: t.ink },
      h3: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: 0, lineHeight: 1.25, color: t.ink },
      h4: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: 0, lineHeight: 1.25, color: t.ink },
      h5: { fontWeight: 600, letterSpacing: 0, color: t.ink },
      h6: { fontWeight: 600, letterSpacing: 0, color: t.ink },
      subtitle1: { fontWeight: 600, color: t.ink },
      subtitle2: { fontWeight: 600, color: t.muted },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { color: t.muted, lineHeight: 1.55 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      // Reusable sentence-case kicker/label style for consumers (sans, no caps).
      overline: {
        fontFamily: UI_FONT,
        fontWeight: 600,
        fontSize: '0.8125rem',
        letterSpacing: 0,
        textTransform: 'none',
        color: t.muted,
        lineHeight: 1.5,
      },
      // Result titles: serif, accent-coloured, used via <Typography variant="resultTitle">
      // (renders as an <h3> through the variantMapping below).
      resultTitle: {
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.3,
        letterSpacing: 0,
        color: t.accent,
      },
    },
    components: {
      MuiTypography: {
        defaultProps: { variantMapping: { resultTitle: 'h3' } },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            backgroundColor: t.bg,
          },
          '::selection': { background: t.accentWeak },
          'input::placeholder, textarea::placeholder': { color: t.faint, opacity: 1 },
          // Query-match highlighting rendered as bold (Trove-style), not a
          // coloured mark; snippet containers carry class `ps-snippet`.
          '.ps-snippet em, .ps-snippet mark': {
            fontStyle: 'normal',
            fontWeight: 700,
            background: 'transparent',
            color: 'inherit',
            padding: 0,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: t.bg,
            color: t.ink,
            backgroundImage: 'none',
            boxShadow: 'none',
            border: 0,
            borderBottom: `1px solid ${t.line}`,
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          // Flat bordered surfaces; kill MUI's dark-mode elevation overlay tint.
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
            border: `1px solid ${t.line}`,
            borderRadius: RADIUS,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: RADIUS,
            border: `1px solid ${t.line}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { border: 0, borderRight: `1px solid ${t.line}`, borderRadius: 0 },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          // Accordions sit inside bordered Papers — no second border.
          root: { border: 0, boxShadow: 'none', borderRadius: 0, '&:before': { display: 'none' } },
        },
      },
      // Floating surfaces keep a shadow.
      MuiPopover: {
        styleOverrides: {
          paper: { boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${t.line}` },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${t.line}` },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { boxShadow: '0 16px 48px rgba(0,0,0,0.18)' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 4 },
          sizeSmall: { fontSize: '0.75rem' },
          label: { letterSpacing: 0 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: RADIUS,
          },
          contained: {
            backgroundColor: t.accent,
            color: t.onAccent,
            '&:hover': { backgroundColor: t.accent, filter: 'brightness(0.92)' },
          },
          outlined: {
            borderColor: t.fieldColor,
            color: t.ink,
            '&:hover': { borderColor: t.ink, backgroundColor: 'transparent' },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: t.line } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.line },
          head: {
            fontFamily: UI_FONT,
            fontWeight: 600,
            fontSize: '0.8125rem',
            letterSpacing: 0,
            textTransform: 'none',
            color: t.muted,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.fieldColor },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.faint },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent, borderWidth: 1.5 },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: 0,
            '&.Mui-selected': { fontWeight: 700, color: t.ink },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: t.accent, height: 3 },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: t.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontFamily: UI_FONT, fontSize: '0.75rem', letterSpacing: 0 },
        },
      },
    },
  });
}

export default buildTheme;
